import { SDK_VERSION_HEADER_NAME } from "./config";
import { canonicalContextJSONStringify, ReflagContext } from "./context";
import { Logger, loggerWithPrefix } from "./logger";

export type EventSourceLike = {
  addEventListener: (type: string, cb: (event: any) => void) => void;
  close: () => void;
};

export type EventSourceFactory = (url: string) => EventSourceLike;

export type PubSubMessage = {
  name?: string;
  event?: string;
  channel?: string;
  data?: any;
  [key: string]: any;
};

export class AblySSEChannel {
  private isOpen = false;
  private eventSource: EventSourceLike | null = null;
  private retryInterval: ReturnType<typeof setInterval> | null = null;
  private logger: Logger;

  constructor(
    private channels: string[],
    private sseBaseUrl: string,
    private messageHandler: (message: PubSubMessage) => void,
    logger: Logger,
    private eventSourceFactory?: EventSourceFactory,
    private publishableKey?: string,
    private sdkVersion?: string,
    private path = "sse",
    private context?: ReflagContext,
    private credentials?: RequestCredentials,
  ) {
    this.logger = loggerWithPrefix(logger, "[SSE]");

    if (!this.sseBaseUrl.endsWith("/")) {
      this.sseBaseUrl += "/";
    }
  }

  private onMessage(e: MessageEvent) {
    let payload: PubSubMessage | undefined;

    try {
      if (e.data) {
        const message = JSON.parse(e.data);
        if (message && typeof message === "object") {
          const parsed = { ...message } as PubSubMessage;
          if (typeof parsed.data === "string") {
            try {
              parsed.data = JSON.parse(parsed.data);
            } catch {
              // keep string payload as-is
            }
          }
          payload = parsed;
        }
      }
    } catch (error: any) {
      this.logger.warn("received unparsable message", error, e);
      return;
    }

    if (payload) {
      this.logger.debug("received message", payload);

      try {
        this.messageHandler(payload);
      } catch (error: any) {
        this.logger.warn("failed to handle message", error, payload);
      }

      return;
    }

    this.logger.warn("received invalid message", e);
  }

  private onOpen(e: Event) {
    this.logger.debug("event source connection opened", e);
  }

  private onError(e: Event) {
    const connectionState = (e as any)?.target?.readyState;

    if (connectionState === 2) {
      this.logger.debug("event source connection closed", e);
    } else if (connectionState === 1) {
      this.logger.warn("event source connection failed to open", e);
    } else {
      this.logger.warn("event source unexpected error occurred", e);
    }

    this.disconnect();
  }

  private createEventSource(url: string): EventSourceLike | undefined {
    if (this.eventSourceFactory) {
      return this.eventSourceFactory(url);
    }

    if (typeof EventSource === "undefined") {
      this.logger.warn(
        "EventSource is not available in this environment; SSE channel cannot be opened",
      );
      return;
    }

    return this.credentials === "include"
      ? new EventSource(url, { withCredentials: true })
      : new EventSource(url);
  }

  public async connect() {
    if (this.isOpen) {
      this.logger.warn("channel connection already open");
      return;
    }

    this.isOpen = true;
    try {
      const url = new URL(this.path, this.sseBaseUrl);
      if (this.channels.length > 0) {
        url.searchParams.append("v", "1.2");
        url.searchParams.append("channels", this.channels.join(","));
        url.searchParams.append("rewind", "1");
      }
      if (this.publishableKey) {
        url.searchParams.append("publishableKey", this.publishableKey);
      }
      if (this.sdkVersion) {
        url.searchParams.append(SDK_VERSION_HEADER_NAME, this.sdkVersion);
      }
      const serializedContext = canonicalContextJSONStringify(this.context);
      if (serializedContext) {
        url.searchParams.append("contextJson", serializedContext);
      }

      this.eventSource = this.createEventSource(url.toString()) ?? null;
      if (!this.eventSource) {
        return;
      }

      this.eventSource.addEventListener("error", (e) => this.onError(e));
      this.eventSource.addEventListener("open", (e) => this.onOpen(e));
      this.eventSource.addEventListener("message", (m) => this.onMessage(m));

      this.logger.debug("channel connection opened");
    } finally {
      this.isOpen = !!this.eventSource;
    }
  }

  public disconnect() {
    if (!this.isOpen) {
      return;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;

      this.logger.debug("channel connection closed");
    }

    this.isOpen = false;
  }

  public open(options?: { retryInterval?: number; retryCount?: number }) {
    if (!this.eventSourceFactory && typeof EventSource === "undefined") {
      this.logger.warn(
        "EventSource is not available in this environment; SSE channel cannot be opened",
      );
      return;
    }

    const retryInterval = options?.retryInterval ?? 1000 * 30;
    const retryCount = options?.retryCount ?? 3;
    let retriesRemaining = retryCount;

    const tryConnect = async () => {
      try {
        await this.connect();
        retriesRemaining = retryCount;
      } catch (e) {
        if (retriesRemaining > 0) {
          this.logger.warn(
            `failed to connect, ${retriesRemaining} retries remaining`,
            e,
          );
        } else {
          this.logger.warn(`failed to connect, no retries remaining`, e);
        }
      }
    };

    void tryConnect();

    this.retryInterval = setInterval(() => {
      if (!this.isConnected() && this.retryInterval) {
        if (retriesRemaining <= 0) {
          clearInterval(this.retryInterval);
          this.retryInterval = null;
          return;
        }

        retriesRemaining--;
        void tryConnect();
      }
    }, retryInterval);
  }

  public close() {
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }

    this.disconnect();
  }

  public isActive() {
    return !!this.retryInterval;
  }

  public isConnected() {
    return this.isOpen && !!this.eventSource;
  }
}

export function openAblySSEChannel({
  channel,
  channels,
  callback,
  sseBaseUrl,
  logger,
  eventSourceFactory,
  publishableKey,
  sdkVersion,
  path,
  context,
  credentials,
}: {
  channel?: string;
  channels?: string[];
  callback: (req: PubSubMessage) => void;
  logger: Logger;
  sseBaseUrl: string;
  eventSourceFactory?: EventSourceFactory;
  publishableKey?: string;
  sdkVersion?: string;
  path?: string;
  context?: ReflagContext;
  credentials?: RequestCredentials;
}) {
  const subscribedChannels = channels ?? (channel ? [channel] : []);
  const sse = new AblySSEChannel(
    subscribedChannels,
    sseBaseUrl,
    callback,
    logger,
    eventSourceFactory,
    publishableKey,
    sdkVersion,
    path,
    context,
    credentials,
  );

  sse.open();

  return sse;
}

export function closeAblySSEChannel(channel: AblySSEChannel) {
  channel.close();
}
